import type { Item } from "../types/items.js";
import { TAG_QUEST, hasAnyTag, hasTag } from "./tags.js";

/**
 * Shop data model + predicates.
 *
 * Matches the authoring shape in `specs/inventory-and-economy.md §Shops`:
 *
 * ```json
 * {
 *   "shopkeeperId": "bartender_goat_inn",
 *   "stock": [{ "item": "ration", "price": 2, "restockOnCamp": true, "quantity": 99 }],
 *   "buysTags": ["weapon", "armor", "potion"],
 *   "buyPriceMultiplier": 0.5
 * }
 * ```
 *
 * `shopBuysItem` and `shopBuyPrice` are the two calls the trade UI needs:
 * the first gates which items show up in the sell pane, the second tells
 * the player (and the transaction) how many copper the shop will pay.
 *
 * Quest items are always refused — the check lives in `shopBuysItem` so no
 * single call-site has to remember to combine `quest` + `buysTags`.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface ShopStockEntry {
  readonly itemId: string;
  /** Sale price in gold pieces (consistent with SRDItem `value`). */
  readonly priceGold: number;
  /** Copies available for purchase. Infinite (`Number.POSITIVE_INFINITY`) = unlimited. */
  readonly quantity: number;
  /** True if this entry refills to its authored quantity on camp tick. */
  readonly restockOnCamp: boolean;
}

export interface Shop {
  readonly shopkeeperId: string;
  readonly stock: readonly ShopStockEntry[];
  /**
   * Tag whitelist — the shop only buys items whose tags intersect this list.
   * Empty array means "refuses everything" (a narrative-only NPC).
   */
  readonly buysTags: readonly string[];
  /**
   * Sell-back rate. `0.5` means the shop pays half the item's `valueCp`.
   * Clamped to `[0, 1]` by `shopBuyPrice`.
   */
  readonly buyPriceMultiplier: number;
}

// ── Predicates ───────────────────────────────────────────────────────────────

/**
 * True when the shop is willing to buy this item.
 *
 *  - Quest items (`tags: ["quest"]`) are always refused.
 *  - Otherwise the item must share at least one tag with `shop.buysTags`.
 *  - A shop with an empty `buysTags` list refuses everything.
 */
export function shopBuysItem(shop: Shop, item: Item): boolean {
  if (hasTag(item, TAG_QUEST)) return false;
  if (shop.buysTags.length === 0) return false;
  return hasAnyTag(item, shop.buysTags);
}

/**
 * Copper pieces the shop pays for a single copy. `Math.floor` after the
 * multiplier so we never pay fractional copper. Returns 0 when the shop
 * refuses the item.
 */
export function shopBuyPrice(shop: Shop, item: Item): number {
  if (!shopBuysItem(shop, item)) return 0;
  const mult = Math.max(0, Math.min(1, shop.buyPriceMultiplier));
  return Math.floor(item.valueCp * mult);
}

/** Subset of `items` the shop will actually accept. */
export function shopAcceptedFromList<T extends Item>(
  shop: Shop,
  items: readonly T[],
): T[] {
  return items.filter((i) => shopBuysItem(shop, i));
}

// ── Transactions (I07) ───────────────────────────────────────────────────────

/**
 * Copper-per-gold conversion used by the purchase path. The rest of the
 * engine already uses copper as the canonical currency; shop stock
 * stores gold prices because that's how SRD items are authored.
 */
export const COPPER_PER_GOLD = 100;

export type BuyFailure =
  | "not_in_stock"
  | "out_of_stock"
  | "insufficient_gold";

export type SellFailure = "not_bought" | "not_in_stock";

export interface BuyResult {
  readonly ok: boolean;
  readonly shop: Shop;
  /** Copper charged; `0` when `ok` is false. */
  readonly costCp: number;
  readonly failure?: BuyFailure;
}

export interface SellResult {
  readonly ok: boolean;
  readonly shop: Shop;
  /** Copper credited to the party; `0` when `ok` is false. */
  readonly proceedsCp: number;
  readonly failure?: SellFailure;
}

function stockIndex(shop: Shop, itemId: string): number {
  return shop.stock.findIndex((e) => e.itemId === itemId);
}

function decrementStock(shop: Shop, index: number): Shop {
  const entry = shop.stock[index]!;
  if (!Number.isFinite(entry.quantity)) return shop;
  const nextStock = [...shop.stock];
  const nextQty = entry.quantity - 1;
  nextStock[index] = { ...entry, quantity: nextQty };
  return { ...shop, stock: nextStock };
}

/**
 * Attempt to buy one copy of `itemId` from the shop with `partyGoldCp`
 * copper. No side effects — caller applies the returned `costCp` to
 * `PartyStore.spendGold` and adds the item to whatever character paid.
 *
 * An entry with `quantity: Infinity` is a bottomless restock (arrows,
 * rations) and does not decrement. Finite entries decrement by one
 * and refuse on reaching zero.
 */
export function buyItem(
  shop: Shop,
  itemId: string,
  partyGoldCp: number,
): BuyResult {
  const idx = stockIndex(shop, itemId);
  if (idx < 0) return { ok: false, shop, costCp: 0, failure: "not_in_stock" };
  const entry = shop.stock[idx]!;
  if (entry.quantity <= 0) {
    return { ok: false, shop, costCp: 0, failure: "out_of_stock" };
  }
  const costCp = entry.priceGold * COPPER_PER_GOLD;
  if (partyGoldCp < costCp) {
    return { ok: false, shop, costCp: 0, failure: "insufficient_gold" };
  }
  return { ok: true, shop: decrementStock(shop, idx), costCp };
}

/**
 * Attempt to sell one copy of `item` to the shop. Returns the copper
 * proceeds (`0` when the shop refuses the item). Sold items are not
 * added back to shop stock — the spec deliberately has no buyback
 * rack. Callers remove the item from the character inventory on
 * success.
 */
export function sellItem(shop: Shop, item: Item): SellResult {
  if (!shopBuysItem(shop, item)) {
    return { ok: false, shop, proceedsCp: 0, failure: "not_bought" };
  }
  return { ok: true, shop, proceedsCp: shopBuyPrice(shop, item) };
}

/**
 * Restock tick — called at camp (C07). Every entry with
 * `restockOnCamp: true` is refilled to its `baseQuantity`. Entries
 * without the flag are left as-is so bottomless stock stays bottomless
 * and genuine unique items stay out of rotation once sold.
 */
export function restockShop(
  shop: Shop,
  baseQuantities: Readonly<Record<string, number>>,
): Shop {
  const nextStock = shop.stock.map((entry) => {
    if (!entry.restockOnCamp) return entry;
    const base = baseQuantities[entry.itemId];
    return base === undefined ? entry : { ...entry, quantity: base };
  });
  return { ...shop, stock: nextStock };
}
